import React from 'react';
import { ChevronProps, DayPicker, TZDate } from 'react-day-picker';
import styled from 'styled-components';

import { useCurrentTheme } from 'app/shared/theme';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
} from 'app/shared/components/atoms/IconLibrary';

import 'react-day-picker/style.css';

interface Props {
  numberOfMonthsToDisplay?: number;
  values?: any;
  onChange: (dates: any) => void;
  enablePastDays?: boolean;
  disableToday?: boolean;
  hasBlackOutDatesStyling?: boolean;
  hasMultiSelect?: boolean;
  isPopover?: boolean;
}

const ArrowLink = styled.div``;

interface NavIconProps {
  orientation?: 'up' | 'down' | 'left' | 'right';
}

const NavIcon: React.FunctionComponent<
  React.PropsWithChildren<NavIconProps>
> = ({ orientation }) => {
  return (
    <div>
      {orientation == 'left' ? (
        <ArrowLink>
          <ArrowLeftIcon iconSize={15} />
        </ArrowLink>
      ) : orientation == 'right' ? (
        <ArrowLink>
          <ArrowRightIcon iconSize={15} />
        </ArrowLink>
      ) : null}
    </div>
  );
};

// Given a date object, returns e.g. '2022-11-16'
const getDateStrFromDate = (date: any) =>
  date.toISOString().split('T')[0] || '';

// Given a string e.g. '2022-11-16', returns a date object
const getDateFromDateStr = (dateStr: string) => {
  const [year, month, day] = dateStr
    .split('-')
    .map((datePart: string) => Number(datePart));

  return new TZDate(year, month - 1, day, 'UTC');
};

// Returns a date object representing midnight UTC start-of-day today
// e.g. if calling this function at 2025-01-13 14:56 EST, returns
// a date object representing 2025-01-13 00:00 UTC
const getTodayDate = () => {
  // e.g. a string '2025-01-13T19:56:00.000Z'
  const todayStr = getDateStrFromDate(new Date());
  // e.g. a date object representing 2025-01-13 00:00 UTC
  return getDateFromDateStr(todayStr);
};

const useStyles = (hasBlackOutDatesStyling?: boolean, isPopover?: boolean) => {
  const theme = useCurrentTheme();

  if (process.env.NODE_ENV === 'test') {
    return '';
  }

  return `
    .rdp-root {
      padding: ${isPopover ? '4px 14px 10px 12px' : undefined};
      --rdp-accent-color: ${hasBlackOutDatesStyling ? theme.colors.blueGrey : theme.colors.orangeCrush};
      --rdp-accent-background-color: ${hasBlackOutDatesStyling ? theme.colors.blueGrey : theme.colors.orangeCrush};
      --rdp-today-color: ${theme.colors.backToBlack};
      --rdp-selected-border: 21px solid ${hasBlackOutDatesStyling ? theme.colors.blueGrey : theme.colors.orangeCrush};
      --rdp-disabled-opacity: 30%;

      ${
        isPopover
          ? `
            .rdp-selected {
              background-color: ${theme.colors.button.primary.background};
              border-radius: 50px;
            }

            .rdp-month_caption {
              .rdp-caption_label {
                padding-left: 12px;
              }
            }`
          : ''
      }
    }
  `;
};

const DatePicker: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  numberOfMonthsToDisplay = 1,
  values = [],
  onChange = () => {},
  enablePastDays = false,
  disableToday = false,
  hasBlackOutDatesStyling = false,
  hasMultiSelect = true,
  isPopover = false,
}) => {
  const styles = useStyles(hasBlackOutDatesStyling, isPopover);

  const handleDayClick = (day: any, modifiers: any = {}) => {
    if (modifiers.disabled) {
      return;
    }

    // clickedDay = a string e.g. '2022-11-16'
    const clickedDay = getDateStrFromDate(day);

    let updatedSelectedDays;

    if (hasMultiSelect) {
      if (modifiers.selected) {
        // User has UNSELECTED the day (yes, confusing - 'modifiers.selected' means they just unselected this day)
        updatedSelectedDays = values.filter(
          (selectedDay: string) => selectedDay !== clickedDay
        );
        onChange(updatedSelectedDays);
      } else {
        // User has SELECTED the day
        updatedSelectedDays = values.concat(clickedDay).sort();
      }
    } else {
      updatedSelectedDays = [clickedDay];
    }

    onChange(updatedSelectedDays);
  };

  const disabledDays = () => {
    const todayDate = getTodayDate();
    const disabledDays: any = [];
    if (!enablePastDays) {
      disabledDays.push({ before: todayDate });
    }
    if (disableToday) {
      disabledDays.push(todayDate);
    }
    return disabledDays;
  };

  return (
    <div>
      <style>{styles}</style>
      <DayPicker
        onDayClick={handleDayClick}
        selected={values.map((dateStr: string) => getDateFromDateStr(dateStr))}
        components={{
          Chevron: (props: ChevronProps) => <NavIcon {...props} />,
        }}
        numberOfMonths={numberOfMonthsToDisplay}
        defaultMonth={
          hasMultiSelect || values.length === 0
            ? undefined
            : getDateFromDateStr(values[0])
        }
        disabled={disabledDays()}
        mode={hasMultiSelect ? 'multiple' : 'single'}
        required={hasMultiSelect ? false : true}
        timeZone="UTC"
        data-qaid="react-day-picker"
      />
    </div>
  );
};

export default DatePicker;
